
    import { Component, Vue, Prop, Watch } from 'vue-property-decorator';

    interface ParsedRow {
        name: string;
        locationGroup: number | null;
        longitude: number;
        latitude: number;
        props: { [propertyId: number]: string };
    }

    @Component
    export default class BulkUploadLocationsDialog extends Vue {
        @Prop({ default: null }) public bulkUploadLocations: any;
        @Prop({ default: null }) public model: any;
        @Prop({ default: 'en-US' }) public fileFormatCulture!: string;

        private modalShow = false;
        private file: File | null = null;
        private scenarioName = '';
        private rows: ParsedRow[] = [];
        private propertyColumns: any[] = [];
        private parseError = '';

        private mounted() {
            this.$on('escKey' + this.$options.name, () => {
                if (this.modalShow) this.close();
            });
            document.addEventListener("keydown", (e) => {
                if (e.key === "Escape") {
                    this.$emit('escKey' + this.$options.name);
                }
            });
        }

        @Watch('bulkUploadLocations')
        private onPayloadChanged(value: any) {
            if (value) {
                this.file = null;
                this.scenarioName = value.defaultScenarioName || '';
                this.rows = [];
                this.propertyColumns = [];
                this.parseError = '';
                this.modalShow = true;
            } else {
                this.modalShow = false;
            }
        }

        private get modelLocationProperties(): any[] {
            return (this.bulkUploadLocations && this.bulkUploadLocations.modelLocationProperties) || [];
        }

        private get locationGroupItems(): any[] {
            if (!this.model || !this.model.locationGroups) return [];
            return this.model.locationGroups.filter((g: any) => g.selectable !== false);
        }

        private get headers() {
            const base = [
                { text: this.$t('message.name'), value: 'name' },
                { text: this.$t('message.locationGroup'), value: 'locationGroup' },
                { text: this.$t('message.longitude'), value: 'longitude' },
                { text: this.$t('message.latitude'), value: 'latitude' },
            ];
            const extra = this.propertyColumns.map(p => ({
                text: p.title || p.name,
                value: 'prop_' + p.id,
                sortable: false,
            }));
            return [...base, ...extra];
        }

        private get invalidRowCount(): number {
            return this.rows.filter(r => !r.name || !r.locationGroup).length;
        }

        private get canSave(): boolean {
            return this.rows.length > 0 && this.invalidRowCount === 0 && !!this.scenarioName && !this.parseError;
        }

        private scenarioNameRule(v: string) {
            return !!v || String(this.$t('message.required'));
        }

        private propSlotName(prop: any): string {
            return 'item.prop_' + prop.id;
        }

        private detectDelimiter(firstLine: string): string {
            if (this.fileFormatCulture && (this.fileFormatCulture.startsWith('nl') || this.fileFormatCulture.startsWith('de') || this.fileFormatCulture.startsWith('fr'))) return ';';
            if (this.fileFormatCulture && this.fileFormatCulture.startsWith('en')) return ',';
            const semi = (firstLine.match(/;/g) || []).length;
            const comma = (firstLine.match(/,/g) || []).length;
            return semi > comma ? ';' : ',';
        }

        private parseNumber(raw: string): number {
            if (raw == null) return NaN;
            const trimmed = raw.trim();
            if (this.fileFormatCulture && (this.fileFormatCulture.startsWith('nl') || this.fileFormatCulture.startsWith('de') || this.fileFormatCulture.startsWith('fr'))) {
                return parseFloat(trimmed.replace(/\./g, '').replace(',', '.'));
            }
            return parseFloat(trimmed.replace(/,/g, ''));
        }

        private async onFileSelected(file: File | null) {
            this.rows = [];
            this.propertyColumns = [];
            this.parseError = '';
            if (!file) return;
            const text = await file.text();
            const lines = text.split(/\r?\n/).filter(l => l.length > 0);
            if (lines.length < 2) {
                this.parseError = String(this.$t('message.uploadLocationsCsvEmpty'));
                return;
            }
            const delimiter = this.detectDelimiter(lines[0]);
            const headerCells = lines[0].split(delimiter).map(h => h.trim());
            const headerLower = headerCells.map(h => h.toLowerCase());
            const nameIdx = headerLower.indexOf('name');
            const lonIdx = headerLower.findIndex(h => h === 'longitude' || h === 'lon' || h === 'lng');
            const latIdx = headerLower.findIndex(h => h === 'latitude' || h === 'lat');
            const groupIdx = headerLower.findIndex(h => h === 'locationgroup' || h === 'location group' || h === 'group');
            if (lonIdx === -1 || latIdx === -1) {
                this.parseError = String(this.$t('message.uploadLocationsCsvMissingColumns'));
                return;
            }

            // Detect columns that match a model location-property by name (case-insensitive).
            // Standard columns (name/longitude/latitude/locationgroup) are excluded so they are not treated as properties.
            const reservedIdx = new Set([nameIdx, lonIdx, latIdx, groupIdx].filter(i => i >= 0));
            const detectedProps: { columnIndex: number; property: any }[] = [];
            for (let i = 0; i < headerCells.length; i++) {
                if (reservedIdx.has(i)) continue;
                const cellLower = headerLower[i];
                const matched = this.modelLocationProperties.find((lp: any) =>
                    (lp.name && lp.name.toLowerCase() === cellLower) ||
                    (lp.title && lp.title.toLowerCase() === cellLower));
                if (matched) detectedProps.push({ columnIndex: i, property: matched });
            }
            this.propertyColumns = detectedProps.map(d => d.property);

            const groups = this.locationGroupItems;
            const parsedRows: ParsedRow[] = [];
            for (let i = 1; i < lines.length; i++) {
                const cols = lines[i].split(delimiter).map(c => c.trim());
                const name = nameIdx >= 0 ? cols[nameIdx] : '';
                const lon = this.parseNumber(cols[lonIdx]);
                const lat = this.parseNumber(cols[latIdx]);
                if (isNaN(lon) || isNaN(lat)) continue;
                let groupId: number | null = null;
                if (groupIdx >= 0) {
                    const raw = cols[groupIdx];
                    if (raw) {
                        const match = groups.find((g: any) =>
                            String(g.id) === raw ||
                            (g.name && g.name.toLowerCase() === raw.toLowerCase()) ||
                            (g.code && g.code.toLowerCase() === raw.toLowerCase()));
                        if (match) groupId = match.id;
                    }
                }
                if (groupId == null && groups.length === 1) groupId = groups[0].id;

                const props: { [id: number]: string } = {};
                for (const dp of detectedProps) {
                    const raw = cols[dp.columnIndex] != null ? cols[dp.columnIndex] : '';
                    props[dp.property.id] = raw;
                }
                parsedRows.push({ name, locationGroup: groupId, longitude: lon, latitude: lat, props });
            }

            if (parsedRows.length === 0) {
                this.parseError = String(this.$t('message.uploadLocationsCsvNoRows'));
                return;
            }
            this.rows = parsedRows;
        }

        private async close() {
            const res = await this.$confirm(String(this.$t('message.confirmClose')), { buttonTrueText: String(this.$t('message.yes')), buttonFalseText: String(this.$t('message.no')) });
            if (res) this.$emit('cancel');
        }

        private submit() {
            const locations = this.rows.map(row => {
                const locationProperties = this.propertyColumns
                    .map(p => {
                        const raw = row.props[p.id];
                        if (raw == null || raw === '') return null;
                        const entry: any = { id: p.id, dataType: p.dataType };
                        if (p.dataType === 0) {
                            entry.stringValue = String(raw);
                        } else {
                            const num = this.parseNumber(String(raw));
                            if (isNaN(num)) return null;
                            entry.numberValue = num;
                        }
                        return entry;
                    })
                    .filter((e: any) => e != null);
                return {
                    name: row.name,
                    locationGroup: row.locationGroup,
                    longitude: row.longitude,
                    latitude: row.latitude,
                    locationProperties,
                };
            });
            this.$emit('save', {
                scenarioId: this.bulkUploadLocations.scenarioId,
                scenarioName: this.scenarioName,
                locations,
            });
        }
    }
